import cx from 'classnames';
import { FormV2 } from 'components/ui';
import { ChevronDown, XClose } from 'ethena-icons';
import { ALL_VALUE } from 'lib/datasets/util';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import RSSelect, {
  ActionMeta,
  ClearIndicatorProps,
  components,
  MultiValue,
  OptionProps,
  Props,
  SingleValue,
} from 'react-select';
import { Checkbox } from './Checkbox';
import css from './Select.module.scss';

export const ClearIndicator = (props: ClearIndicatorProps) => (
  <div aria-hidden className={css.clearIndicator} {...props.innerProps}>
    <XClose width={15} height={15} />
  </div>
);

export interface ISelectOption<T = any> {
  label: string;
  value: T;
  isDisabled?: boolean;
}

const MultiSelectOption = (props: OptionProps) => (
  <components.Option {...props}>
    <Checkbox
      checked={props.isSelected}
      label={props.label}
      disabled={props.isDisabled}
    />
  </components.Option>
);

const SingleSelectOption = (props: OptionProps<ISelectOption>) => {
  const id = _.kebabCase(props.label);

  return (
    <components.Option
      {...props}
      innerProps={{
        ...props.innerProps,
        'aria-selected': props.isSelected,
        id,
        onMouseMove: _.noop,
        onMouseOver: _.noop,
      }}
    />
  );
};

const SelectAllOption = (props: OptionProps<ISelectOption>) => {
  const { isSelected, label, isDisabled } = props;

  return (
    <components.Option {...props}>
      <Checkbox checked={isSelected} disabled={isDisabled} label={label} />
    </components.Option>
  );
};

interface ISelectProps extends Props<ISelectOption> {
  alignMenu?: 'left' | 'right';
  errorMessage?: string;
  includeSelectAll?: boolean;
  isFluidWidth?: boolean;
  isInvalid?: boolean;
}

export const Select = ({
  alignMenu = 'left',
  className,
  components,
  defaultValue,
  errorMessage,
  includeSelectAll,
  isFluidWidth,
  isInvalid,
  isMulti,
  options,
  id: inputId,
  value,
  onChange,
  ...rest
}: ISelectProps) => {
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  // to avoid a visual delay in the select all checkbox, we are handling
  // this logic in the handleOnChange. This useEffect is to catch for when
  // values are updated from the parent component like a Clear All button
  useEffect(() => {
    if (isMulti && includeSelectAll && value) {
      const nonDisabledOptions = _.filter(
        options,
        (option: ISelectOption) => !option.isDisabled
      );
      const allSelected =
        _.isArray(value) && _.size(nonDisabledOptions) === _.size(value);
      setIsSelectAllChecked(allSelected);
    } else {
      setIsSelectAllChecked(false);
    }
  }, [value, isMulti, includeSelectAll, options]);

  const handleSelectAllChange = (selected: boolean) => {
    setIsSelectAllChecked(selected);

    if (selected) {
      const nonDisabledOptions = _.filter(
        options,
        (option: ISelectOption) => !option.isDisabled
      );
      onChange(nonDisabledOptions as MultiValue<ISelectOption>, null);
    } else {
      onChange([], null);
    }
  };

  const handleOnChange = (
    newValue: MultiValue<ISelectOption> | SingleValue<ISelectOption>,
    action: ActionMeta<ISelectOption>
  ) => {
    if (isMulti && includeSelectAll && newValue) {
      const nonDisabledOptions = _.filter(
        options,
        (option: ISelectOption) => !option.isDisabled
      );
      const allSelected =
        _.isArray(newValue) && _.size(nonDisabledOptions) === _.size(newValue);
      setIsSelectAllChecked(allSelected);
    }
    onChange(newValue, action);
  };

  const selectAllOption: ISelectOption = {
    label: 'Select All',
    value: ALL_VALUE,
  };

  const allOptions =
    isMulti && includeSelectAll ? [selectAllOption, ...options] : options;

  const selectComponents = {
    ClearIndicator,
    IndicatorSeparator: () => null,
    DropdownIndicator: () => <ChevronDown aria-hidden="true" />,
    Option: (props: OptionProps<ISelectOption>) => (
      <SingleSelectOption {...props} />
    ),
    ...components,
  };

  const multiSelectComponents = useMemo(
    () => ({
      ...selectComponents,
      Option: (props: OptionProps<ISelectOption>) => {
        if (props.data.value === ALL_VALUE) {
          return (
            <SelectAllOption
              {...props}
              isSelected={isSelectAllChecked}
              innerProps={{
                ...props.innerProps,
                onClick: () => handleSelectAllChange(!isSelectAllChecked),
              }}
            />
          );
        }
        return <MultiSelectOption {...props} />;
      },
      MultiValueRemove: ({ innerProps }: OptionProps<ISelectOption>) => (
        <div
          {...innerProps}
          className={cx(css.multiValueRemove, innerProps.className)}
        >
          <XClose width={12} height={12} />
        </div>
      ),
      ClearIndicator: null,
    }),
    [isSelectAllChecked]
  );

  return (
    <div
      className={cx(css.select, className, css[`menu-${alignMenu}`], {
        [css.fluid]: isFluidWidth,
        [css.multi]: isMulti,
        [css.invalid]: isInvalid,
      })}
    >
      <RSSelect
        classNamePrefix="s"
        closeMenuOnSelect={!isMulti}
        components={isMulti ? multiSelectComponents : selectComponents}
        defaultValue={defaultValue}
        hideSelectedOptions={false}
        isMulti={isMulti}
        onChange={handleOnChange}
        options={allOptions}
        value={value}
        {...rest}
      />
      <FormV2.Control.Feedback type="invalid">
        {errorMessage}
      </FormV2.Control.Feedback>
    </div>
  );
};
